import React, { useEffect, useState } from 'react';
import { PackageOpen, ShoppingBag, ArrowRight, Zap, Truck } from 'lucide-react';
import { getUsernameFromProfileId } from '../../../../services/firestoreService';
import { db } from '../../../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import useBlockStyles from '../common/useBlockStyles';
import { trackEvent } from '../../../../utils/analytics';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'left',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

/**
 * SingleProductRenderer - Renders a single product showcase
 * Digital products → Stripe Checkout. POD products → product detail page.
 */
const SingleProductRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const {
    heading,
    productId,
    buttonText = 'Buy Now',
    showDescription = true,
    layout = 'imageLeft',
  } = content;

  const [product, setProduct] = useState(null);
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  useEffect(() => {
    const loadProduct = async () => {
      if (!productId || !profileId) {
        setLoading(false);
        return;
      }

      try {
        const [productSnap, fetchedUsername] = await Promise.all([
          getDoc(doc(db, 'products', productId)),
          getUsernameFromProfileId(profileId),
        ]);

        if (productSnap.exists()) {
          setProduct({ id: productSnap.id, ...productSnap.data() });
        }
        setUsername(fetchedUsername);
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId, profileId]);

  // Helper to get best product image
  const getProductImage = (prod) => {
    if (prod.product_type === 'pod' && prod.variants?.length > 0) {
      const firstVariant = prod.variants[0];
      if (firstVariant.mockup_images && firstVariant.variant_map) {
        const blackVariantKey = Object.keys(firstVariant.variant_map).find((key) =>
          key.startsWith('black_')
        );
        if (blackVariantKey) {
          const blackPrintifyVariantId = firstVariant.variant_map[blackVariantKey];
          const blackMockup = firstVariant.mockup_images.find(
            (img) => img.variant_ids && img.variant_ids.includes(blackPrintifyVariantId)
          );
          if (blackMockup?.src) return blackMockup.src;
        }
      }
      return prod.preview_image || firstVariant?.mockup_images_urls?.[0] || null;
    }
    return prod.preview_image || prod.imageUrl || null;
  };

  const formatPrice = (prod) => {
    if (prod.product_type === 'pod' && prod.variants?.length > 0) {
      const prices = prod.variants.map((v) => v.price).filter(Boolean);
      if (prices.length > 0) {
        const min = Math.min(...prices);
        return `From $${(min / 100).toFixed(2)}`;
      }
    }
    if (prod.price) return `$${(prod.price / 100).toFixed(2)}`;
    return '';
  };

  // Handle checkout for digital products
  const handleCheckout = async () => {
    if (!product) return;

    setCheckingOut(true);
    try {
      const response = await fetch(
        'https://us-central1-chess-web-host.cloudfunctions.net/createShopCheckoutSession',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: product.id,
            origin: window.location.origin,
            cancelReturnTo: window.location.pathname,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error('Checkout failed:', { status: response.status, error: data.error, details: data.details });
        const errorMessage = data.details || data.error || 'Failed to start checkout. Please try again.';
        alert(errorMessage);
        setCheckingOut(false);
        return;
      }

      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Something went wrong. Please try again.');
      setCheckingOut(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse rounded-xl overflow-hidden border border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="bg-gray-800 h-64" />
              <div className="p-6 space-y-4">
                <div className="h-8 bg-gray-800 rounded w-3/4" />
                <div className="h-6 bg-gray-800 rounded w-1/4" />
                <div className="h-4 bg-gray-800 rounded w-full" />
                <div className="h-4 bg-gray-800 rounded w-5/6" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No product selected or product not found
  if (!product) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <PackageOpen size={48} className="mx-auto mb-4" style={{ color: styles.mutedColor }} />
          <p style={{ color: styles.mutedColor }}>
            {productId ? 'Product not found' : 'No product selected'}
          </p>
        </div>
      </div>
    );
  }

  const imageUrl = getProductImage(product);
  const price = formatPrice(product);
  const isPOD = product.product_type === 'pod';
  const productPageUrl = username ? `/product/${username}/${product.id}` : null;

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Heading */}
        {heading && (
          <h3
            className={`${styles.headingSizeClass} font-bold ${styles.headingAlignmentClass} mb-6 sm:mb-8 md:mb-10`}
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            {heading}
          </h3>
        )}

        {/* Product Card — CSS Grid layout (same pattern as TextImageRenderer) */}
        <div
          className="rounded-xl"
          style={{
            backgroundColor: styles.cardBackground,
            border: `1px solid ${styles.borderColor}`,
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 items-center">
            {/* Image column */}
            <div className={`${layout === 'imageLeft' ? 'md:order-1' : 'md:order-2'} flex justify-center p-6`}>
              <div className="relative">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.title}
                    className="max-w-full h-auto object-contain rounded-xl"
                    style={{ maxHeight: '400px', maxWidth: '400px' }}
                  />
                ) : (
                  <div
                    className="w-full h-56 flex items-center justify-center"
                    style={{ backgroundColor: styles.sectionBackground }}
                  >
                    <PackageOpen size={48} style={{ color: styles.mutedColor }} />
                  </div>
                )}

                {/* Delivery type badge */}
                <div className="absolute top-2 right-2">
                  {isPOD ? (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-0.5"
                      style={{ backgroundColor: '#3b82f6', color: '#ffffff' }}
                    >
                      <Truck size={10} />
                      Ships
                    </span>
                  ) : (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-0.5"
                      style={{ backgroundColor: '#10b981', color: '#ffffff' }}
                    >
                      <Zap size={10} />
                      Instant
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Info column */}
            <div className={`${layout === 'imageLeft' ? 'md:order-2' : 'md:order-1'} p-6 sm:p-8 space-y-3`}>
              <h4
                className="text-xl sm:text-2xl md:text-3xl font-bold"
                style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
              >
                {product.title}
              </h4>

              {price && (
                <p
                  className="text-lg sm:text-xl font-semibold"
                  style={{ color: styles.accentColor }}
                >
                  {price}
                </p>
              )}

              {showDescription && product.description && (
                <p
                  className="text-sm sm:text-base line-clamp-4"
                  style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
                >
                  {product.description}
                </p>
              )}

              {/* Buy Now / View Product button */}
              <div className="pt-2">
                {isPOD && productPageUrl ? (
                  <a
                    href={productPageUrl}
                    onClick={() => trackEvent('Product Click', { coach: username, productId: product.id })}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-base transition-colors hover:opacity-90"
                    style={styles.getButtonStyles()}
                  >
                    <ShoppingBag size={18} />
                    {buttonText}
                    <ArrowRight size={16} />
                  </a>
                ) : (
                  <button
                    onClick={() => { trackEvent('Checkout Start', { coach: username, type: 'shop' }); trackEvent('Add to Cart', { coach: username, productId: product.id }); handleCheckout(); }}
                    disabled={checkingOut}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-base transition-colors hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={styles.getButtonStyles()}
                  >
                    <ShoppingBag size={18} />
                    {checkingOut ? 'Processing...' : buttonText}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleProductRenderer;
